/**
 * Deployed solutions (names only, from `deployments` rows) and visit history
 * (`property_visit_history`), both read from the database.
 *
 * Type sizes follow the portal floor of 11px for an uppercase eyebrow and 12px
 * for body copy; the earlier 10px labels were unreadable on a phone.
 */
import type { PropertyVisit, SolutionType } from '../lib/propertyCatalog';
import { solutionLink } from '@pazportal/ui';
import { formatSolutionType } from '../lib/formatters';
import { useI18n } from '../i18n/I18nProvider';

export type SolutionTone = 'green' | 'amber' | 'red';

/**
 * A plain list of deployed solutions (names only); a chip links to the
 * solution's paznetworks.com page when one exists. `status` adds a health dot.
 */
export function DeployedSolutionList({
  solutions,
  status,
}: {
  solutions: readonly SolutionType[];
  status?: Partial<Record<SolutionType, SolutionTone>>;
}) {
  const { t } = useI18n();
  if (solutions.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-2" aria-label={t('property.solutions.title')}>
      {solutions.map((type) => {
        const tone = status?.[type];
        const href = solutionLink(type);
        const dot = tone && (
          <span
            aria-hidden="true"
            className={`h-2 w-2 rounded-full ${tone === 'green' ? 'bg-success' : tone === 'amber' ? 'bg-warning' : 'bg-notification'}`}
          />
        );
        const label = (
          <span className="text-sm text-text">
            {formatSolutionType(type)}
            {/* The dot is the only visual carrier of state, so the state is
                named for anyone who cannot use hue to read it. */}
            {tone && (
              <span className="sr-only">
                {' — '}
                {tone === 'green'
                  ? t('dashboard.systems_state_operational')
                  : tone === 'amber'
                    ? t('dashboard.systems_state_degraded')
                    : t('dashboard.systems_state_offline')}
              </span>
            )}
          </span>
        );
        return (
          <li key={type}>
            {href ? (
              <a
                href={href}
                target="_blank"
                rel="noreferrer"
                title="Learn more on paznetworks.com"
                className="flex items-center gap-2 rounded-full bg-surface-3 px-3 py-1.5 hover:bg-surface-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {dot}
                {label}
                <svg className="h-3 w-3 text-text-faint" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                <span className="sr-only"> (opens paznetworks.com)</span>
              </a>
            ) : (
              <span className="flex items-center gap-2 rounded-full bg-surface-3 px-3 py-1.5">
                {dot}
                {label}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function PropertyTripList({
  visits,
  note,
  preview = 6,
  earlierLabel,
}: {
  visits: PropertyVisit[];
  note?: string | null;
  preview?: number;
  earlierLabel: string;
}) {
  const shown = visits.slice(0, preview);
  const rest = visits.slice(preview);
  return (
    <div>
      {note && <p className="mb-2 text-xs leading-relaxed text-text-muted">{note}</p>}
      <TripItems visits={shown} />
      {rest.length > 0 && (
        <details className="mt-2 rounded-md border border-border">
          <summary className="property-resource-link cursor-pointer rounded-md px-3 py-2.5 text-xs font-medium">
            {earlierLabel} ({rest.length})
          </summary>
          <div className="border-t border-border px-3 py-2">
            <TripItems visits={rest} />
          </div>
        </details>
      )}
    </div>
  );
}

function TripItems({ visits }: { visits: PropertyVisit[] }) {
  return (
    <ol className="space-y-2.5">
      {visits.map((visit) => (
        <li key={visit.id} className="border-l-2 border-primary/40 pl-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-muted">
            {visit.windowLabel}
          </p>
          <p className="mt-0.5 text-xs leading-relaxed text-text">{visit.title}</p>
        </li>
      ))}
    </ol>
  );
}
